import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { getPendingCount, subscribeToPending } from '@/utils/offlineSync';

/**
 * useOfflineStatus — observation-only view of connectivity + the offline
 * replay queue (Batch 14). Combines three signals:
 *   1. navigator.onLine (initial connectivity)
 *   2. the window online/offline events (live transitions)
 *   3. the offlineSync queue size (mutations waiting to replay)
 *
 * It never touches the sync/replay logic in reviewSlice.syncOfflineReviews —
 * it only reads getPendingCount() and reacts to its transitions. The
 * "synced N" signal is derived purely from the queue draining to zero:
 * clearOfflineQueue() (which drives the count to 0) is called ONLY by a
 * successful flush, so a drop from a nonzero peak down to 0 is an unambiguous
 * "flush completed" event — no hook into the sync function required.
 */

function subscribeConnectivity(callback: () => void): () => void {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);
  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
}

function getOnlineSnapshot(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

export interface OfflineStatusState {
  /** Browser currently reports no network connection. */
  isOffline: boolean;
  /** Reviews + card updates + settings deltas queued for replay. */
  pendingCount: number;
  /** How many mutations the most recent flush drained. 0 until the first flush. */
  syncedCount: number;
  /** Bumps once per completed flush so a toast can re-trigger. 0 = never fired. */
  syncedKey: number;
}

export function useOfflineStatus(): OfflineStatusState {
  const isOnline = useSyncExternalStore(subscribeConnectivity, getOnlineSnapshot, () => true);
  const pendingCount = useSyncExternalStore(subscribeToPending, getPendingCount, () => 0);

  const [synced, setSynced] = useState({ count: 0, key: 0 });
  const prevCountRef = useRef(pendingCount);
  // Highest pending count observed during the current offline accumulation.
  // Reset to 0 each time the queue fully drains, so the toast reports the size
  // of the batch that was just flushed rather than a single delta.
  const peakRef = useRef(pendingCount);

  useEffect(() => {
    const prev = prevCountRef.current;
    if (pendingCount > peakRef.current) peakRef.current = pendingCount;

    // Drained to empty after having held work: a flush completed. (Edge case:
    // if a flush partially fails, entries are re-queued AFTER the queues clear,
    // so the count momentarily hits 0 and this may briefly over-report before
    // the re-queue lands. Acceptable for a transient status toast, and it keeps
    // this observer fully decoupled from the replay logic.)
    if (pendingCount === 0 && prev > 0 && peakRef.current > 0) {
      const flushed = peakRef.current;
      peakRef.current = 0;
      setSynced((s) => ({ count: flushed, key: s.key + 1 }));
    }

    prevCountRef.current = pendingCount;
  }, [pendingCount]);

  return {
    isOffline: !isOnline,
    pendingCount,
    syncedCount: synced.count,
    syncedKey: synced.key,
  };
}
